// src/components/auth/LoginForm.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './LoginForm.css';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="28" height="28">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const LoginForm = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('mr');
  const { login, accountBlocked, clearAccountBlocked } = useAuth();

  const text = {
    en: {
      welcome: 'Welcome',
      subtitle: 'Your personal fitness coach is ready!',
      phone: 'Phone Number',
      password: 'Password',
      login: 'Login',
      help: 'Need help? Contact us',
      invalidPhone: 'Please enter a valid 10-digit phone number',
      invalidCredentials: 'Invalid phone number or password',
      loggingIn: 'Logging in...',
      required: 'This field is required',
      accountInactive: 'Your login is currently disabled. Please contact the team using the WhatsApp button on the screen.',
      passwordShort: 'Password must be at least 6 characters'
    },
    mr: {
      welcome: 'स्वागत',
      subtitle: 'तुमचा वैयक्तिक फिटनेस प्रशिक्षक तयार आहे!',
      phone: 'फोन नंबर',
      password: 'पासवर्ड',
      login: 'लॉगिन',
      help: 'मदत हवी आहे? आमच्याशी संपर्क साधा',
      invalidPhone: 'कृपया वैध 10-अंकी फोन नंबर टाका',
      invalidCredentials: 'चुकीचा फोन नंबर किंवा पासवर्ड',
      loggingIn: 'लॉगिन करत आहे...',
      required: 'हे फील्ड आवश्यक आहे',
      accountInactive: 'तुमचा लॉगिन सध्या बंद आहे. कृपया स्क्रीनवरील WhatsApp बटण वापरून आमच्या टीमशी संपर्क साधा.',
      passwordShort: 'पासवर्ड किमान 6 अक्षरांचा असावा'
    }
  };

  const t = text[language];

  const validate = () => {
    if (!phone.trim()) { setError(`${t.required} - ${t.phone}`); return false; }
    if (phone.length !== 10) { setError(t.invalidPhone); return false; }
    if (!password.trim()) { setError(`${t.required} - ${t.password}`); return false; }
    if (password.length < 6) { setError(t.passwordShort); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    clearAccountBlocked();
    if (!validate()) return;

    setLoading(true);
    try {
      await login(phone, password);
    } catch (err) {
      if (err.message === 'ACCOUNT_INACTIVE') {
        setError(t.accountInactive);
      } else {
        setError(t.invalidCredentials);
      }
    }
    setLoading(false);
  };

  const handleWhatsApp = () => {
    const message = language === 'en'
      ? 'Hi, I need help with Spocademy login'
      : 'नमस्कार, मला Spocademy लॉगिनसाठी मदत हवी';
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="login-container">
      <button
        className="lang-toggle-floating"
        onClick={() => setLanguage(language === 'en' ? 'mr' : 'en')}
        type="button"
      >
        {language === 'en' ? 'मराठी' : 'EN'}
      </button>

      <div className="login-form">
        <div className="brand-logo">
          <div className="logo-container">
            <img
              src="/logo.png"
              alt="Spocademy"
              className="logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="logo-fallback">Spocademy</span>
          </div>
          <h1>{t.welcome}</h1>
          <p>{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="phone">{t.phone}</label>
            <input
              id="phone"
              type="tel"
              placeholder="9876543210"
              value={phone}
              onChange={(e) => {
                setError('');
                setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
              }}
              disabled={loading}
              autoComplete="tel"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t.password}</label>
            <input
              id="password"
              type="password"
              placeholder={t.password}
              value={password}
              onChange={(e) => {
                setError('');
                setPassword(e.target.value);
              }}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {(error || accountBlocked) && <div className="error-message">{error || t.accountInactive}</div>}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !phone || !password}
          >
            {loading ? (
              <span className="btn-spinner-row">
                <span className="btn-spinner" />
                {t.loggingIn}
              </span>
            ) : t.login}
          </button>
        </form>

        <div className="help-text">
          <p>{t.help}</p>
        </div>
      </div>

      <div className="whatsapp-float" onClick={handleWhatsApp} title="Contact Support">
        <WhatsAppIcon />
      </div>
    </div>
  );
};

export default LoginForm;